import { getCompanies } from "api";
import { getInstanceSetting } from "api/admin";
import {
    deleteDirectory,
    deleteFile,
    getDocumentList,
    getHomeDirectory,
    getDirectoryPermissions,
} from "api/canshare/canshareApi";
import { useNotificationProvider } from "hooks";
import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectUserInfo } from "redux/userInfo/userInfoSlice"; // Adjust the path accordingly

const useFileManager = () => {
    const [fileList, setFileList] = useState([]);
    const [currDirId, setCurrDirId] = useState(undefined);
    const historyRef = useRef([]);
    const [checked, setChecked] = useState({ dir: [], files: [] });
    const [pending, setPending] = useState(false);
    const [custId, setCustId] = useState(undefined);
    const [copyMoveAction, setCopyMoveAction] = useState(undefined);
    const [copyMoveClipboard, setCopyMoveClipboard] = useState([]);
    const [openUploadModal, setOpenUploadModal] = useState(false);
    const userInfo = useSelector(selectUserInfo);
    // Legacy and role-based flags
    const [isAdmin, setIsAdmin] = useState(false);
    const [isExecutive, setIsExecutive] = useState(false);
    const [isUser, setIsUser] = useState(false);
    const [isRenamingFileDir, setIsRenamingFileDir] = useState(undefined);
    const [allowClientAccessPoint, setAllowClientAccessPoint] = useState(false);

    // New state to check if the current user has write permissions
    const [hasWritePermission, setHasWritePermission] = useState(true);
    const userId = userInfo?.userInfo?.id; // Adjust if needed

    const resetToDefaultState = () => {
        historyRef.current = [];
        setCurrDirId(undefined);
        setOpenUploadModal(false);
        setCopyMoveAction(undefined);
        setCopyMoveClipboard([]);
        setIsRenamingFileDir(undefined);
        setChecked({ dir: [], files: [] });
    };

    const { notifyError, notifyWarning, notifySuccess } =
        useNotificationProvider();

    useEffect(() => {
        const roles = userInfo?.userInfo?.roles || [];
        setIsAdmin(roles.includes("CANshareV3_Admin"));
        setIsExecutive(roles.includes("CANshareV3_Executive"));
        setIsUser(roles.includes("CANshareV3_User"));

        const userCustId = Number(userInfo?.userInfo?.customerId) || 0;
        if (userCustId === 0) setAllowClientAccessPoint(true);

        getInstanceSetting("canshare", "default_customer_dir")
            .then((v) => setCustId(Number(v) || userCustId))
            .catch(() => setCustId(userCustId));
    }, [userInfo]);

    const handleUploadModalOpen = () => {
        setOpenUploadModal(true);
    };

    const handleUploadModalClose = () => {
        setOpenUploadModal(false);
    };

    const clearCopyMoveClipboard = () => {
        setCopyMoveAction(false);
        setCopyMoveClipboard(undefined);
    };

    const addFileToCopyMoveClipboard = (action = "move") => {
        const clipboard = [];
        checked?.dir?.forEach((item) => {
            clipboard.push(item);
        });
        checked?.files?.forEach((item) => {
            clipboard.push(item);
        });
        setCopyMoveAction(action);
        setCopyMoveClipboard(clipboard);
    };

    const addToNavHistory = (obj) => {
        const historyStack = historyRef.current;
        historyStack.push(obj);
        historyRef.current = historyStack;
    };

    const removeFromNavHistory = () => {
        const historyStack = historyRef.current;
        const removedObj = historyStack.pop();
        historyRef.current = historyStack;
        return removedObj;
    };

    const changeCurrentDirectory = (dir) => {
        addToNavHistory(dir);
        setCurrDirId(dir.vv_dir_id);
    };

    const traverseBackDirectory = (dirFromHistory = undefined) => {
        if (dirFromHistory) {
            let history = historyRef.current;
            while (
                history[history.length - 1].vv_dir_id !==
                dirFromHistory.vv_dir_id
            ) {
                removeFromNavHistory();
                history = historyRef.current;
            }
        } else {
            removeFromNavHistory();
        }
        const historyStack = historyRef.current;
        setCurrDirId(historyStack[historyStack.length - 1].vv_dir_id);
    };

    const handleCheckedToggle = (
        value,
        file = true,
        clearOtherChecks = false
    ) => {
        if (!file) {
            if (clearOtherChecks) {
                setChecked({
                    dir: [value],
                    files: [],
                });
                return;
            }
            const currentIndex = checked?.dir?.indexOf(value);
            const newChecked = [...checked.dir];
            if (currentIndex === -1) {
                newChecked.push(value);
            } else {
                newChecked.splice(currentIndex, 1);
            }
            setChecked((old) => ({ ...old, dir: newChecked }));
        } else {
            if (clearOtherChecks) {
                setChecked({
                    dir: [],
                    files: [value],
                });
                return;
            }
            const currentIndex = checked?.files?.indexOf(value);
            const newChecked = [...checked.files];
            if (currentIndex === -1) {
                newChecked.push(value);
            } else {
                newChecked.splice(currentIndex, 1);
            }
            setChecked((old) => ({ ...old, files: newChecked }));
        }
    };

    const toggleCheckedOnAllItems = () => {
        const checkedItems = { dir: [], files: [] };
        if (checked.files.length > 0 || checked.dir.length > 0) {
            setChecked({ dir: [], files: [] });
            return;
        }
        const dir = [];
        const files = [];
        fileList.forEach((item) => {
            if (item.isDir === 1) {
                dir.push(item);
            } else {
                files.push(item);
            }
            checkedItems.dir = dir;
            checkedItems.files = files;
        });
        setChecked(checkedItems);
    };

    const handleDelete = async () => {
        if (!isAdmin && !isExecutive) {
            notifyError(
                "You do not have permission to delete files or directories."
            );
            return;
        }

        const successful = { dir: [], files: [] };
        const failed = { dir: [], files: [] };

        const dirPromises = checked?.dir.map(async (dir) => {
            const res = await deleteDirectory(dir.vv_dir_id);
            if (res.Success) {
                successful.dir.push(dir);
            } else {
                failed.dir.push(dir);
            }
        });

        const filePromises = checked?.files.map(async (file) => {
            const res = await deleteFile(file.vv_file_id);
            if (res.Success) {
                successful.files.push(file);
            } else {
                failed.files.push(file);
            }
        });

        await Promise.all([...dirPromises, ...filePromises]);

        let summaryMessage = "";
        if (successful.dir.length > 0) {
            summaryMessage += `Successfully deleted ${successful.dir.length} directories. `;
        }
        if (successful.files.length > 0) {
            summaryMessage += `Successfully deleted ${successful.files.length} files. `;
        }
        if (failed.dir.length > 0) {
            summaryMessage += `Failed to delete ${
                failed.dir.length
            } directories: ${failed.dir.map((dir) => dir.name).join(", ")}. `;
        }
        if (failed.files.length > 0) {
            summaryMessage += `Failed to delete ${
                failed.files.length
            } files: ${failed.files.map((file) => file.name).join(", ")}. `;
        }

        if (
            (successful.dir.length > 0 || successful.files.length > 0) &&
            !(failed.dir.length > 0 || failed.files.length > 0)
        ) {
            notifySuccess(summaryMessage);
        } else if (
            (successful.dir.length > 0 || successful.files.length > 0) &&
            (failed.dir.length > 0 || failed.files.length > 0)
        ) {
            notifyWarning(summaryMessage);
        } else {
            notifyError(summaryMessage);
        }
        fetchLatestFileList();
    };

    const fetchLatestFileList = async () => {
        setPending(true);
        let files = await getDocumentList(custId, currDirId);
        const shouldHideFinancialFolder =
            !isExecutive &&
            typeof window !== "undefined" &&
            window.location.href?.includes("CAMFM");
        if (shouldHideFinancialFolder) {
            files = files.filter((item) => {
                if (
                    item.isDir === 1 &&
                    item.name.toLowerCase().includes("financial")
                ) {
                    return false;
                }
                return true;
            });
        }
        setFileList(files);
        setChecked({ dir: [], files: [] });
        setPending(false);
    };

    useEffect(() => {
        const getHomeDir = async () => {
            setPending(true);
            const result = await getHomeDirectory(custId);
            addToNavHistory(result);
            setCurrDirId(result.vv_dir_id);
            setPending(false);
        };

        if (custId && custId !== 0) {
            resetToDefaultState();
            getHomeDir();
        }
    }, [custId]);

    useEffect(() => {
        if (currDirId) {
            setChecked({ dir: [], files: [] });
            fetchLatestFileList();
        }
    }, [currDirId]);

    // Updated effect to check write permission:
    // Now, if the user is admin, executive, or marked as canShareAdmin, they always have write permission.
    useEffect(() => {
        async function checkWritePermission() {
            if (isAdmin || isExecutive || userInfo?.userInfo?.isCanShareAdmin) {
                setHasWritePermission(true);
                return;
            }
            if (currDirId && custId && userId) {
                try {
                    const permsResponse = await getDirectoryPermissions(
                        currDirId,
                        custId
                    );
                    let permissionsArray = [];
                    if (Array.isArray(permsResponse)) {
                        permissionsArray = permsResponse;
                    } else if (
                        permsResponse.data &&
                        Array.isArray(permsResponse.data)
                    ) {
                        permissionsArray = permsResponse.data;
                    }
                    const userPerm = permissionsArray.find(
                        (perm) => perm.UserId === userId
                    );
                    if (userPerm) {
                        setHasWritePermission(userPerm.cbWrite === "1");
                    } else {
                        setHasWritePermission(false);
                    }
                } catch (error) {
                    console.error("Error checking permissions", error);
                    setHasWritePermission(false);
                }
            }
        }
        checkWritePermission();
    }, [
        currDirId,
        custId,
        userId,
        isAdmin,
        isExecutive,
        userInfo?.userInfo?.isCanShareAdmin,
    ]);

    return {
        fileList,
        setFileList,
        currDirId,
        checked,
        pending,
        setPending,
        custId,
        setCustId,
        historyRef,
        traverseBackDirectory,
        changeCurrentDirectory,
        handleCheckedToggle,
        copyMoveAction,
        copyMoveClipboard,
        addFileToCopyMoveClipboard,
        clearCopyMoveClipboard,
        fetchLatestFileList,
        openUploadModal,
        handleUploadModalOpen,
        handleUploadModalClose,
        isAdmin,
        isExecutive,
        isUser,
        toggleCheckedOnAllItems,
        handleDelete,
        isRenamingFileDir,
        setIsRenamingFileDir,
        allowClientAccessPoint,
        hasWritePermission,
    };
};

export default useFileManager;
